/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.uploadReceipt

import features.uploadReceipt.internal.UploadReceiptUseCase
import features.uploadReceipt.internal.UploadReceiptUseCase.Params
import features.uploadReceipt.models.UploadReceiptResponseModel
import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.krMultiPartForm.UploadReceiptRequestModel
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.json.Json
import kotlin.js.ExperimentalJsExport

typealias UploadReceiptState = CFlow<UploadReceiptUseCaseResult?>

typealias UploadReceiptUseCaseResult = Result<UploadReceiptResponseModel>

fun UploadReceiptUseCaseResult.serializeUploadReceiptApiResult(): SerializedResult<String> {
    return this.toJsonResult<UploadReceiptResponseModel>()
}

/**
 * Serializes [UploadReceiptState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun UploadReceiptState.serializeUploadReceiptState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<UploadReceiptResponseModel>()
    }.asCFlow()
}

/**
 * upload an receipt [Api] class
 */
class UploadReceiptApi : Api() {

    val uploadReceiptState: UploadReceiptState
        get() = _uploadReceiptState.asCFlow()

    companion object {
        private val _uploadReceiptState: MutableStateFlow<UploadReceiptUseCaseResult> = MutableStateFlow(Idle())
    }

    private val uploadReceiptUseCase = UploadReceiptUseCase()

    /**
     * upload an receipt
     */
    suspend fun uploadReceipt(
        overrideHeaders: Map<String, String>? = null,
        businessId: String,
        uploadReceiptBodyRequest: UploadReceiptRequestModel
    ): Result<UploadReceiptResponseModel> {
        /* set state to Loading as soon as function is called */
        _uploadReceiptState.value = Loading()
        val result = this.uploadReceiptUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                businessId = businessId,
                uploadReceiptBodyRequest = uploadReceiptBodyRequest
            )
        ) { result ->
            _uploadReceiptState.value = result
            return@uploadReceiptUseCase result
        }
        return result
    }

    /**
    * upload an receipt
    *
    * This method is used by the Native artifact when dealing with object body params
    */
    suspend fun uploadReceipt(
        overrideHeaders: Map<String, String>? = null,
        methods: Map<String, Any>
    ): Result<UploadReceiptResponseModel> {
        return uploadReceipt(
            overrideHeaders = overrideHeaders,
            businessId = methods.getValue("businessId") as String,
            uploadReceiptBodyRequest = Json.decodeFromString(
                methods.getValue("uploadReceiptBodyRequest") as String
            )
        )
    }
}